"use client";

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { SiteFlavor } from "@/lib/site-flavor";
import { type ClientBrand } from "@/lib/brand";

interface SiteFlavorContextValue {
  flavor: SiteFlavor;
  isGetmint: boolean;
  billingEnabled: boolean;
  paymentsEnabled: boolean;
  emailOnlyAuth: boolean;
  brand: ClientBrand;
}

// No default brand: a literal here would import the registry, and importing the
// registry from a client component inlines *both* brands into the bundle — which
// is exactly how mint24's Telegram URL and Russian OG alt reached getmintai.app's
// JavaScript. The provider wraps the whole app from the root layout, so the only
// way to read a null context is to render a consumer outside it.
const SiteFlavorContext = createContext<SiteFlavorContextValue | null>(null);

export function SiteFlavorProvider({
  flavor,
  brand,
  emailOnlyAuth = false,
  children,
}: {
  flavor: SiteFlavor;
  emailOnlyAuth?: boolean;
  /**
   * Resolved on the server so only this flavor's brand reaches the bundle, and
   * narrowed to {@link ClientBrand} so server-only fields — the analytics vendor
   * among them — stay out of the page payload.
   */
  brand: ClientBrand;
  children: ReactNode;
}) {
  const value = useMemo<SiteFlavorContextValue>(
    () => ({
      flavor,
      isGetmint: flavor === "getmintai",
      // Subscription UI and limits stay visible while checkout is paused.
      billingEnabled: true,
      paymentsEnabled: flavor !== "getmintai",
      emailOnlyAuth,
      brand,
    }),
    [flavor, brand, emailOnlyAuth],
  );

  return (
    <SiteFlavorContext.Provider value={value}>
      {children}
    </SiteFlavorContext.Provider>
  );
}

export function useSiteFlavor(): SiteFlavorContextValue {
  const value = useContext(SiteFlavorContext);
  if (!value) {
    throw new Error("useSiteFlavor must be used inside <SiteFlavorProvider>");
  }
  return value;
}
